import { useState, useEffect, useCallback, useRef } from 'react';
import { 
    createEmptyTree, createDefaultRule, createCompoundNode, 
    insertNode, removeNode, updateNode, findNode, 
    countRules, flattenRules, replaceNode, generateId
} from '../utils/filters/treeUtils';

export const useFilterState = ({ initialTree = null, schema = [], onChange }) => {
    const [tree, setTree] = useState(initialTree);
    const isLocalChangeRef = useRef(false);

    // Sync from props if external control updates it, with deep comparison to avoid circular loops
    useEffect(() => {
        if (initialTree !== undefined && initialTree !== null) {
            if (JSON.stringify(initialTree) !== JSON.stringify(tree)) {
                setTree(initialTree);
            }
        }
    }, [initialTree, tree]);

    // Send updates upwards ONLY when change was triggered locally (by user action)
    useEffect(() => {
        if (isLocalChangeRef.current) {
            isLocalChangeRef.current = false;
            if (onChange) {
                onChange(tree);
            }
        }
    }, [tree, onChange]);

    const setTreeLocal = useCallback((updater) => {
        isLocalChangeRef.current = true;
        setTree(updater);
    }, []);

    const addRule = useCallback((parentId) => {
        isLocalChangeRef.current = true;
        setTree(prev => {
            const rule = createDefaultRule(schema);
            if (!prev) {
                const root = createEmptyTree();
                return insertNode(root, root.id, rule);
            }
            return insertNode(prev, parentId || prev.id, rule);
        });
    }, [schema]);

    const addGroup = useCallback((parentId, operator = 'AND') => {
        isLocalChangeRef.current = true;
        setTree(prev => {
            const group = createCompoundNode(operator);
            const rule = createDefaultRule(schema);
            group.children.push(rule); // Auto-add one rule

            if (!prev) return group;
            return insertNode(prev, parentId || prev.id, group);
        });
    }, [schema]);

    const updateRule = useCallback((ruleId, patch) => {
        isLocalChangeRef.current = true;
        setTree(prev => updateNode(prev, ruleId, patch));
    }, []);

    const removeNodeById = useCallback((nodeId) => {
        isLocalChangeRef.current = true;
        setTree(prev => {
            const newTree = removeNode(prev, nodeId);
            if (!newTree || (newTree.type === 'compound' && newTree.children.length === 0)) {
                return createEmptyTree();
            }
            return newTree;
        });
    }, []);

    const toggleGroupOperator = useCallback((groupId) => {
        isLocalChangeRef.current = true;
        setTree(prev => {
            const node = findNode(prev, groupId);
            if (node && node.type === 'compound') {
                return updateNode(prev, groupId, { operator: node.operator === 'AND' ? 'OR' : 'AND' });
            }
            return prev;
        });
    }, []);

    const turnIntoGroup = useCallback((ruleId) => {
        isLocalChangeRef.current = true;
        setTree(prev => {
            const rule = findNode(prev, ruleId);
            if (!rule || rule.type !== 'rule') return prev;

            const newGroup = createCompoundNode('AND');
            const clonedRule = { ...rule, id: generateId() };
            const newRule = createDefaultRule(schema);

            newGroup.children = [clonedRule, newRule];

            if (prev.id === ruleId) {
                return newGroup;
            }

            return replaceNode(prev, ruleId, newGroup);
        });
    }, [schema]);

    const clearAll = useCallback(() => {
        isLocalChangeRef.current = true;
        setTree(createEmptyTree());
    }, []);

    const activeRuleCount = countRules(tree);
    const isFiltered = tree !== null && activeRuleCount > 0;

    return {
        tree,
        setTree: setTreeLocal,
        addRule,
        addGroup,
        updateRule,
        removeNode: removeNodeById,
        toggleGroupOperator,
        clearAll,
        activeRuleCount,
        isFiltered,
        turnIntoGroup
    };
};
